import { useEffect, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { useMailbox } from "../../context/MailboxContext";
import { useChatUnread } from "../../context/ChatUnreadContext";
import { setApplicationBadge } from "../../native/appBadge";

/** Mounted within the authenticated providers; unread data owns the iOS badge. */
export default function NativeBadgeSync() {
  const mailbox = useMailbox();
  const chats = useChatUnread();
  const [active, setActive] = useState(true);
  const [freshAfter, setFreshAfter] = useState(Date.now);
  const activeRef = useRef(true);
  const sourcesRef = useRef({ mailbox, chats });
  sourcesRef.current = { mailbox, chats };

  useEffect(() => {
    if (Capacitor.getPlatform() !== "ios") return undefined;
    activeRef.current = true;
    const onAppState = (event) => {
      const isActive = event?.detail?.isActive !== false;
      activeRef.current = isActive;
      setActive(isActive);
      if (!isActive) return;
      setFreshAfter(Date.now());
      // Resume only after both sources have reconciled. Cached pre-push counts
      // must not overwrite the number set by APNs while the app was closed.
      const { mailbox: currentMailbox, chats: currentChats } = sourcesRef.current;
      currentMailbox.refetch().catch(() => {});
      currentChats.refresh().catch(() => {});
    };
    window.addEventListener("vg:native-app-state", onAppState);
    return () => {
      activeRef.current = false;
      window.removeEventListener("vg:native-app-state", onAppState);
      setApplicationBadge(0, { clearOnLogout: true }).catch(() => {});
    };
  }, []);

  useEffect(() => {
    if (!active || !activeRef.current || !mailbox.unreadReady || !chats.unreadReady) return;
    if (mailbox.unreadUpdatedAt < freshAfter || chats.unreadUpdatedAt < freshAfter) return;
    setApplicationBadge(mailbox.unreadCount + chats.totalUnread).catch(() => {});
  }, [active, freshAfter, mailbox.unreadReady, mailbox.unreadUpdatedAt, mailbox.unreadCount, chats.unreadReady, chats.unreadUpdatedAt, chats.totalUnread]);

  return null;
}
